/**
 * Terms of Service, Privacy Policy and Refund & Cancellation Policy.
 * Business details come from legal/legalDetails.ts.
 */

import React from 'react';
import { BUSINESS, LEGAL_LAST_UPDATED } from '../../legal/legalDetails';
import { PLANS } from '../../utils/plans';
import LegalLinks from './LegalLinks';

export type LegalDocument = 'terms' | 'privacy' | 'refunds';

const TITLES: Record<LegalDocument, string> = {
  terms: 'Terms of Service',
  privacy: 'Privacy Policy',
  refunds: 'Refunds & Cancellation Policy',
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <section className="mt-8">
    <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
    <div className="mt-2 space-y-3 text-sm leading-relaxed text-slate-700">
      {children}
    </div>
  </section>
);

const planSummary = PLANS.map(
  (plan) => `${plan.name} at £${plan.monthlyPrice} per month`
).join(' and ');

const Terms: React.FC = () => (
  <>
    <Section title="1. Who we are">
      <p>
        {BUSINESS.productName} is provided by {BUSINESS.legalName}
        {BUSINESS.companyNumber && ` (company number ${BUSINESS.companyNumber})`},{' '}
        {BUSINESS.address} (&quot;we&quot;, &quot;us&quot;). You can contact
        us at {BUSINESS.contactEmail}.
      </p>
      <p>
        These terms apply when you create an account or buy a subscription.
        Please read them, together with our{' '}
        <a href="/privacy">Privacy Policy</a> and{' '}
        <a href="/refunds">Refunds &amp; Cancellation Policy</a>, before you
        subscribe.
      </p>
    </Section>

    <Section title="2. The service">
      <p>
        {BUSINESS.productName} is an AI interview practice tool. An AI
        interviewer asks questions, reviews your answers and code, and gives
        scored feedback. Scores and feedback are generated automatically by AI.
        They can be wrong or incomplete, are for practice only, and are not
        professional, careers or recruitment advice. We don&apos;t guarantee any
        interview or job outcome.
      </p>
      <p>
        You must be at least 16 years old and give accurate details when you
        sign up. Keep your password private; you&apos;re responsible for activity
        on your account.
      </p>
    </Section>

    <Section title="3. Plans and prices">
      <p>
        We offer two monthly plans: {planSummary}. The features in each plan
        are described on our <a href="/pricing">pricing page</a>. Prices are in
        pounds sterling (GBP). {BUSINESS.vatNote}.
      </p>
      <p>
        If we change a plan&apos;s price we&apos;ll email you at least 30 days
        before the new price applies to you, and you can cancel before it does.
      </p>
    </Section>

    <Section title="4. Payment and automatic renewal">
      <p>
        Payments are processed by Stripe. We never see or store your full card
        details. When you subscribe you pay the first month straight away.{' '}
        <strong>
          Your subscription then renews automatically every month, on the same
          date, and you&apos;ll be charged the monthly price until you cancel.
        </strong>
      </p>
      <p>
        If a payment fails, Stripe will retry it for a short period. If it
        still can&apos;t be collected, your subscription ends and you lose
        access to paid features.
      </p>
    </Section>

    <Section title="5. Changing plan">
      <p>
        You can switch between Standard and Pro at any time from your Plan
        &amp; billing page. An upgrade starts immediately and you pay the
        difference for the rest of the current month. A downgrade also starts
        immediately and the unused difference is credited against your next
        payment.
      </p>
    </Section>

    <Section title="6. Cancelling">
      <p>
        <strong>
          You can cancel at any time from your Plan &amp; billing page, or by
          emailing {BUSINESS.contactEmail}.
        </strong>{' '}
        Cancelling stops future payments. You keep access until the end of the
        month you&apos;ve already paid for. We don&apos;t refund part-used
        months, except as set out in section 7 or where the law requires it.
      </p>
    </Section>

    <Section title="7. Your 14-day right to cancel">
      <p>
        If you&apos;re a consumer in the UK, the Consumer Contracts
        Regulations 2013 give you 14 days from the day you first subscribe to
        cancel. When you subscribe you ask us to start the service straight
        away. If you cancel within those 14 days, we&apos;ll refund what you
        paid, minus a proportionate amount for the days of service you had
        before you told us you were cancelling.
      </p>
      <p>
        To use this right, cancel from your Plan &amp; billing page and email{' '}
        {BUSINESS.contactEmail} asking for a refund, or simply email us a clear
        statement that you want to cancel. We&apos;ll refund you within 14 days
        using the payment method you used.
      </p>
    </Section>

    <Section title="8. Acceptable use">
      <p>You agree not to:</p>
      <ul className="list-disc space-y-1 pl-5">
        <li>break the law or infringe anyone&apos;s rights using the service;</li>
        <li>
          upload content you don&apos;t have the right to share, or other
          people&apos;s personal data without their permission;
        </li>
        <li>
          try to get around plan limits, usage caps or security, or share your
          account;
        </li>
        <li>
          scrape, copy, resell or reverse engineer the service, or use it to
          build a competing product;
        </li>
        <li>
          use the service to generate abusive, harmful or misleading content.
        </li>
      </ul>
    </Section>

    <Section title="9. Your content">
      <p>
        You own what you put into the service: answers, code, CVs, job
        descriptions, case studies and recordings. You give us permission to
        store and process it only to provide the service to you, including
        sending it to our AI and hosting providers as described in our{' '}
        <a href="/privacy">Privacy Policy</a>.
      </p>
    </Section>

    <Section title="10. Availability and changes">
      <p>
        We aim to keep the service available but can&apos;t promise it will
        always be uninterrupted or error-free. We may improve or change
        features. If we make a change that significantly reduces what your
        plan includes, we&apos;ll tell you and you can cancel and get a
        refund for any period you&apos;ve paid for but not received.
      </p>
    </Section>

    <Section title="11. Our responsibility to you">
      <p>
        If we fail to comply with these terms, we&apos;re responsible for loss
        or damage you suffer that is a foreseeable result of our failure. We
        aren&apos;t responsible for business losses such as lost profit or
        lost opportunity. Nothing in these terms limits our liability for
        death or personal injury caused by our negligence, for fraud, or for
        anything else that can&apos;t be limited by law, and nothing affects
        your legal rights as a consumer, including under the Consumer Rights
        Act 2015.
      </p>
    </Section>

    <Section title="12. Suspending or ending your account">
      <p>
        We may suspend or close an account that seriously or repeatedly breaks
        these terms. If we close your account without good reason we&apos;ll
        refund any period you&apos;ve paid for but not received.
      </p>
    </Section>

    <Section title="13. Changes to these terms">
      <p>
        We may update these terms. We&apos;ll tell you about important changes
        at least 30 days in advance. If you don&apos;t agree, you can cancel
        before they take effect.
      </p>
    </Section>

    <Section title="14. Law and disputes">
      <p>
        These terms are governed by the law of England and Wales. You can bring
        a claim in the courts of England and Wales, or if you live in Scotland
        or Northern Ireland, in your local courts. Please contact us first at{' '}
        {BUSINESS.contactEmail} so we can try to put things right.
      </p>
    </Section>
  </>
);

const Privacy: React.FC = () => (
  <>
    <Section title="1. Who is responsible for your data">
      <p>
        {BUSINESS.legalName}, {BUSINESS.address}, is the controller of your
        personal data. Contact us about privacy at {BUSINESS.contactEmail}.
      </p>
    </Section>

    <Section title="2. What we collect">
      <ul className="list-disc space-y-1 pl-5">
        <li>
          <strong>Account details:</strong> your email address and a securely
          hashed password. We never store your password itself.
        </li>
        <li>
          <strong>Subscription details:</strong> your plan, subscription status,
          renewal date, your Stripe customer reference, and a record of when
          you accepted these terms. Your card details are handled by Stripe and
          never reach us.
        </li>
        <li>
          <strong>Interview content:</strong> your spoken and typed answers,
          transcripts, code, AI feedback and scores, and anything you choose to
          upload, such as a CV, job description, case study or architecture
          diagram.
        </li>
        <li>
          <strong>Recordings (Pro, optional):</strong> camera and audio
          recordings, only if you start a recording.
        </li>
        <li>
          <strong>Free demo:</strong> we don&apos;t ask for any personal
          details. We keep a one-way hash of your IP address to limit demos per
          day; the demo transcript is deleted after 24 hours and the rate-limit
          record after 48 hours.
        </li>
        <li>
          <strong>Device storage:</strong> we keep your sign-in session in your
          browser&apos;s local storage. We don&apos;t use advertising or
          tracking cookies. Stripe sets its own cookies on the checkout page to
          prevent fraud.
        </li>
      </ul>
    </Section>

    <Section title="3. Why we use it and our lawful basis">
      <ul className="list-disc space-y-1 pl-5">
        <li>
          To provide the service and your subscription: necessary for our
          contract with you.
        </li>
        <li>
          To take payments, prevent fraud and keep financial records: contract
          and legal obligation.
        </li>
        <li>
          To keep the service secure and improve it: our legitimate interests.
        </li>
      </ul>
      <p>We don&apos;t sell your data or use it for advertising.</p>
    </Section>

    <Section title="4. Who we share it with">
      <p>We use these providers to run the service:</p>
      <ul className="list-disc space-y-1 pl-5">
        <li>Stripe: payments and billing.</li>
        <li>
          OpenAI: generating interview questions, feedback, transcription and
          voice. Content sent through the API isn&apos;t used to train their
          models.
        </li>
        <li>Amazon Web Services (London region): hosting, database and recordings.</li>
        <li>Pinecone: searching your uploaded CV, job description or case study.</li>
        <li>Netlify: hosting the website.</li>
        <li>Google: calendar scheduling, only if you connect your calendar.</li>
      </ul>
      <p>
        Some of these providers process data in the United States. Where they
        do, we rely on UK-approved safeguards such as the International Data
        Transfer Addendum or the UK Extension to the EU-US Data Privacy
        Framework.
      </p>
    </Section>

    <Section title="5. How long we keep it">
      <p>
        We keep your account and interview content while your account is open.
        If you ask us to delete your account we&apos;ll delete it within 30
        days, except billing records, which we must keep for 6 years for tax
        purposes. Free demo data is deleted automatically as described above.
      </p>
    </Section>

    <Section title="6. Your rights">
      <p>
        You can ask to access, correct, delete or export your data, or to
        restrict or object to how we use it. Email {BUSINESS.contactEmail}.
        If you&apos;re unhappy with how we handle your data you can complain
        to the Information Commissioner&apos;s Office (ico.org.uk).
      </p>
    </Section>

    <Section title="7. Security and changes">
      <p>
        Data is encrypted in transit, and passwords are hashed. We&apos;ll
        update this policy if our practices change and tell you about
        important changes.
      </p>
    </Section>
  </>
);

const Refunds: React.FC = () => (
  <>
    <Section title="Cancel any time">
      <p>
        Cancel from your Plan &amp; billing page whenever you like, or email{' '}
        {BUSINESS.contactEmail}. There&apos;s no cancellation fee. You keep
        access until the end of the month you&apos;ve paid for, and you
        won&apos;t be charged again.
      </p>
    </Section>

    <Section title="14-day cooling-off period">
      <p>
        If you cancel within 14 days of first subscribing, you&apos;re entitled
        to a refund of what you paid, minus a proportionate amount for the days
        you had access before cancelling. For example, if you cancel a £30 Pro
        subscription after 3 days of a 30-day month, we&apos;ll refund £27.
      </p>
      <p>
        Cancel from your Plan &amp; billing page and email us to ask for the
        refund. We&apos;ll process it within 14 days to your original payment
        method.
      </p>
    </Section>

    <Section title="After 14 days">
      <p>
        We don&apos;t refund part-used months after the cooling-off period.
        This doesn&apos;t affect your rights under the Consumer Rights Act
        2015: if the service isn&apos;t provided with reasonable care and
        skill, or isn&apos;t as described, contact us and we&apos;ll fix it or
        give you an appropriate refund.
      </p>
    </Section>

    <Section title="Changing plan">
      <p>
        Upgrades start immediately and you pay the difference for the rest of
        the month. Downgrades start immediately and the unused difference is
        credited against your next payment.
      </p>
    </Section>

    <Section title="Failed payments">
      <p>
        If a renewal payment fails, Stripe retries it for a short time. If it
        can&apos;t be collected, your subscription ends and no further charges
        are attempted.
      </p>
    </Section>
  </>
);

const CONTENT: Record<LegalDocument, React.FC> = {
  terms: Terms,
  privacy: Privacy,
  refunds: Refunds,
};

interface LegalPageProps {
  document: LegalDocument;
}

const LegalPage: React.FC<LegalPageProps> = ({ document }) => {
  const Content = CONTENT[document];
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4">
          <a href="/pricing" className="font-semibold text-slate-900 no-underline">
            {BUSINESS.productName}
          </a>
          <a href="/pricing" className="text-sm text-indigo-600">
            Plans &amp; pricing
          </a>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-5 py-10 [&_a]:text-indigo-600 [&_a]:underline">
        <h1 className="text-3xl font-bold text-slate-900">{TITLES[document]}</h1>
        <p className="mt-2 text-sm text-slate-500">
          Last updated {LEGAL_LAST_UPDATED}
        </p>
        <Content />
      </main>
      <footer className="border-t border-slate-200 py-6 text-slate-500">
        <LegalLinks />
      </footer>
    </div>
  );
};

export default LegalPage;
